import {
  getAdminMemberProfile,
  getMemberProfileCatalogs,
  updateAdminMemberProfile,
  type AdminMemberProfileContract,
  type MemberProfileCatalogsResponse,
} from '@urbanik/api-client';
import { CanonicalModal, Icon } from '@urbanik/ui';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
export function ExpandedMemberProfile({
  memberId,
  canUpdate,
  embedded = false,
}: {
  memberId: string;
  canUpdate: boolean;
  embedded?: boolean;
}) {
  const [p, setP] = useState<AdminMemberProfileContract | null>(null),
    [cats, setCats] = useState<MemberProfileCatalogsResponse | null>(null),
    [edit, setEdit] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [form, setForm] = useState<Record<string, any>>({});
  useEffect(() => {
    const c = new AbortController();
    setError('');
    getAdminMemberProfile(apiBase, memberId, c.signal)
      .then((a) => {
        setP(a.profile);
        setForm({ ...a.profile });
      })
      .catch((cause: unknown) => {
        if (c.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : 'No pudimos cargar el perfil ampliado.');
      });
    getMemberProfileCatalogs(apiBase, c.signal)
      .then(setCats)
      .catch(() => {
        if (c.signal.aborted) return;
        setCats({ catalogs: [] });
      });
    return () => c.abort();
  }, [memberId]);
  if (error)
    return (
      <section className="member-detail-grid">
        <article>
          <p role="alert">{error}</p>
        </article>
      </section>
    );
  if (!p || !cats)
    return (
      <section className="member-detail-grid">
        <article>
          <p>Cargando perfil ampliado…</p>
        </article>
      </section>
    );
  const label = (catalog: string, id: string | null) =>
    cats.catalogs.find((c) => c.code === catalog)?.options.find((o) => o.id === id)?.label ??
    'No registrado';
  const field = (key: string, labelText: string, type = 'text') => (
    <label>
      {labelText}
      <input
        className="ui-input"
        type={type}
        value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </label>
  );
  const select = (key: string, labelText: string, catalog: string) => (
    <label>
      {labelText}
      <select
        className="ui-select"
        value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value || null })}
      >
        <option value="">No registrado</option>
        {cats.catalogs
          .find((c) => c.code === catalog)
          ?.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
      </select>
    </label>
  );
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await updateAdminMemberProfile(apiBase, memberId, {
        expectedMemberVersion: p.memberVersion,
        expectedProfileVersion: p.profileVersion,
        nationalId: p.nationalId,
        givenNames: p.givenNames,
        familyNames: p.familyNames,
        birthDate: form.birthDate || undefined,
        civilStatusOptionId: form.civilStatusOptionId || undefined,
        isHeadOfHousehold: form.isHeadOfHousehold === true,
        educationLevelOptionId: form.educationLevelOptionId || undefined,
        isCurrentlyStudying: form.isCurrentlyStudying === true,
        educationInstitutionTypeOptionId: form.isCurrentlyStudying
          ? form.educationInstitutionTypeOptionId || undefined
          : undefined,
        email: form.email || undefined,
        primaryPhone: form.primaryPhone || undefined,
        secondaryPhone: form.secondaryPhone || undefined,
      });
      setP(r.profile);
      setForm({ ...r.profile });
      setEdit(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos guardar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      className={`member-profile-expanded${embedded ? ' member-admin-component--embedded' : ''}`}
    >
      <div className="member-profile-section-heading">
        <div>
          <p className="kicker">Perfil ampliado</p>
          <h2>Datos personales</h2>
        </div>
        {canUpdate && !edit ? (
          <button
            className="ui-button ui-button--primary"
            onClick={() => setEdit(true)}
            aria-label="Editar perfil"
          >
            <Icon name="edit" />
          </button>
        ) : null}
      </div>
      {edit ? (
        <CanonicalModal
          open={edit}
          size="large"
          eyebrow="Perfil ampliado"
          title="Editar datos personales"
          description="Actualiza únicamente la información administrativa autorizada."
          onClose={() => {
            if (busy) return;
            setForm({ ...p });
            setError('');
            setEdit(false);
          }}
        >
          <form
            className="member-create-grid member-profile-edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <section className="member-profile-protected" aria-labelledby="protected-profile-data">
              <div className="member-profile-protected-heading">
                <div>
                  <p className="kicker">Identificación protegida</p>
                  <h3 id="protected-profile-data">Datos no editables</h3>
                </div>
                <Icon name="lock" />
              </div>
              <dl>
                <div>
                  <dt>Cédula</dt>
                  <dd>{p.nationalId}</dd>
                </div>
                <div>
                  <dt>Nombres</dt>
                  <dd>{p.givenNames}</dd>
                </div>
                <div>
                  <dt>Apellidos</dt>
                  <dd>{p.familyNames}</dd>
                </div>
              </dl>
            </section>
            {field('birthDate', 'Fecha de nacimiento', 'date')}
            {select('civilStatusOptionId', 'Estado civil', 'civil_status')}
            {select('educationLevelOptionId', 'Nivel educativo', 'education_level')}
            <label>
              ¿Cabeza de hogar?
              <select
                className="ui-select"
                value={String(form.isHeadOfHousehold ?? '')}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isHeadOfHousehold: e.target.value === '' ? null : e.target.value === 'true',
                  })
                }
              >
                <option value="">No registrado</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </label>
            <label>
              ¿Estudia actualmente?
              <select
                className="ui-select"
                value={String(form.isCurrentlyStudying ?? '')}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isCurrentlyStudying: e.target.value === '' ? null : e.target.value === 'true',
                  })
                }
              >
                <option value="">No registrado</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </label>
            {form.isCurrentlyStudying
              ? select(
                  'educationInstitutionTypeOptionId',
                  'Tipo de institución',
                  'education_institution_type',
                )
              : null}
            {field('email', 'Correo', 'email')}
            {field('primaryPhone', 'Teléfono principal')}
            {field('secondaryPhone', 'Teléfono secundario')}
            <div className="member-status-actions">
              <button
                type="button"
                className="ui-button"
                onClick={() => {
                  setForm({ ...p });
                  setEdit(false);
                }}
              >
                Cancelar
              </button>
              <button className="ui-button ui-button--primary" disabled={busy}>
                {busy ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
            {error ? <p role="alert">{error}</p> : null}
          </form>
        </CanonicalModal>
      ) : (
        <dl className="member-profile-summary">
          <div>
            <dt>Fecha de nacimiento</dt>
            <dd>{p.birthDate ?? 'No registrado'}</dd>
          </div>
          <div>
            <dt>Estado civil</dt>
            <dd>{label('civil_status', p.civilStatusOptionId)}</dd>
          </div>
          <div>
            <dt>Cabeza de hogar</dt>
            <dd>
              {p.isHeadOfHousehold === null ? 'No registrado' : p.isHeadOfHousehold ? 'Sí' : 'No'}
            </dd>
          </div>
          <div>
            <dt>Nivel educativo</dt>
            <dd>{label('education_level', p.educationLevelOptionId)}</dd>
          </div>
          <div>
            <dt>Estudia actualmente</dt>
            <dd>
              {p.isCurrentlyStudying === null
                ? 'No registrado'
                : p.isCurrentlyStudying
                  ? 'Sí'
                  : 'No'}
            </dd>
          </div>
          <div>
            <dt>Correo</dt>
            <dd>{p.email ?? 'No registrado'}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
